<script setup lang="ts">
import type { DynamicAuctionBanner } from '@autobid/strapi-integration/rest/components'
import StrapiImage from '@autobid/ui/components/common/strapi/Image.vue'
import AuctionBannerBgText from '@autobid/ui/componentsGlobal/dynamic/auctionBanner/AuctionBannerBgText.vue'
import AuctionDescriptions from '@autobid/ui/componentsGlobal/dynamic/auctionList/shared/AuctionDescriptions.vue'
import StartDateCalendarPopper from '@autobid/ui/componentsGlobal/dynamic/auctionList/shared/StartDateCalendarPopper.vue'
import { useCarPageInfo } from '@autobid/ui/composables/car/useCarPageInfo'
import { useAuction } from '@autobid/ui/composables/useAuction'
import { useAuctionId } from '@autobid/ui/composables/useAuctionId'
import { useGetTypeOfPrevPage } from '@autobid/ui/composables/useGetTypeOfPrevPage'
import { formatStartDate } from '@autobid/ui/utils/auctions/formatStartDate'
import { formatDate } from '@autobid/ui/utils/date/formatDate'
import { getDayName } from '@autobid/ui/utils/date/getDayName'
import { parseLinkToRelative } from '@autobid/ui/utils/parseLinkToRelative'
import { onMounted, onServerPrefetch } from 'vue'
import { Vue3SlideUpDown } from 'vue3-slide-up-down'

interface Props {
  data: DynamicAuctionBanner
}

const props = defineProps<Props>()

const auctionId = useAuctionId()
const { auction, suspense } = useAuction(auctionId, { suspense: true })
const { pageType, backUrl } = useCarPageInfo()
const { back, options, push } = useRouter()
const { getTypeOfPrevPage } = useGetTypeOfPrevPage()
const routerBackUrl = options.history.state.back
const urlToPreviousPage = computed(() => {
  const typeOfPrevPage = getTypeOfPrevPage()

  if (typeOfPrevPage !== 'cms') {
    if (backUrl.value) {
      return backUrl.value
    }

    if (props.data.auctionListUrl) {
      return parseLinkToRelative(props.data.auctionListUrl)
    }

    if (pageType.value === 'carList') {
      return undefined
    }
  }

  if (String(routerBackUrl)?.length) {
    return String(routerBackUrl)
  }

  return ''
})

const isMobileDescActive = ref(false)
const initialized = ref(false)

const { locale } = useI18n()

function goPreviousPage() {
  if (urlToPreviousPage.value === routerBackUrl) {
    return back()
  }

  return push({ path: urlToPreviousPage.value })
}

onMounted(() => {
  initialized.value = true
})
onServerPrefetch(suspense)
await suspense()
</script>

<template>
  <header
    v-if="auction"
    class="full-width-component relative overflow-visible border-y"
    :class="
      props.data.layout === 'bmw' ? 'lg:min-h-[180px]' : 'lg:min-h-[140px]'
    "
  >
    <img
      v-if="auction.barometerBanner"
      :src="`${auction.barometerBanner}?v=${auction.publicationInfo.dataHash.auction}`"
      :alt="auction?.title ?? ''"
      class="absolute hidden h-full w-full object-cover lg:block"
    >
    <template v-else>
      <StrapiImage
        v-if="data.image"
        :data="data.image"
        :alt="auction?.title ?? ''"
        class="absolute hidden h-full w-full object-cover xl:block"
        :class="{ 'lg:block': !data.imageMobile }"
      />
      <StrapiImage
        v-if="data.imageMobile"
        :data="data.imageMobile"
        :alt="auction?.title ?? ''"
        class="absolute hidden h-full w-full object-cover lg:block xl:hidden"
      />
    </template>
    <div
      v-if="auction"
      class="container relative z-30 mx-auto flex h-full min-h-[inherit] w-full gap-4 overflow-visible bg-primary px-2 py-4 text-white lg:bg-transparent lg:px-4 lg:text-primary"
      :class="{
        'pt-20 lg:pt-40': data.withNavbarPadding,
        'lg:hidden': data.layout === 'bmw',
      }"
    >
      <div
        class="flex h-full w-full flex-col lg:flex-row lg:items-center lg:justify-between"
      >
        <div
          v-if="data.showAuctionLogos"
          class="hidden flex-wrap gap-2 p-4 lg:flex lg:w-1/6"
        >
          <img
            v-for="logo in auction.logos"
            :key="logo.url"
            :src="logo.url"
            class="h-12"
            :alt="logo.alt"
          >
        </div>
        <div
          class="relative flex flex-col justify-center overflow-visible lg:p-4"
        >
          <div class="flex justify-between">
            <div class="flex items-center">
              <button
                v-if="urlToPreviousPage"
                class="lg:hidden"
                @click="goPreviousPage"
              >
                <CommonIconArrow class="-mt-1 mr-3 h-10 w-10" />
              </button>

              <h1 class="!text-white lg:!text-primary">
                {{ auction.title }}
              </h1>

              <div
                v-if="auction.descriptions.length"
                class="-mt-1 flex items-center justify-between lg:hidden"
              >
                <button @click="isMobileDescActive = !isMobileDescActive">
                  <Icon
                    mode="svg"
                    name="mdi:chevron-down"
                    class="text-4xl transition"
                    :class="{
                      'rotate-180': isMobileDescActive,
                    }"
                  />
                </button>
              </div>
            </div>
            <div class="flex text-3xl text-black lg:hidden">
              <DynamicAuctionItemListFiltersMobileButton
                v-if="pageType === 'carList'"
              />
            </div>
          </div>

          <Vue3SlideUpDown
            v-if="auction.descriptions.length"
            v-model="isMobileDescActive"
            class="-mt-2 lg:hidden [@media(min-width:350px)]:mt-0"
            :duration="250"
          >
            <div v-show="initialized">
              <AuctionDescriptions
                :class="{
                  '[@media(min-width:350px)]:pl-14': urlToPreviousPage,
                }"
                :descriptions="auction.descriptions"
              />
            </div>
          </Vue3SlideUpDown>

          <AuctionDescriptions
            class="hidden max-w-xl lg:block xl:max-w-2xl"
            :descriptions="auction.descriptions"
          />
          <AuctionBannerBgText
            v-if="data.leftBackgroundColor?.name"
            :font-color="data.leftBackgroundColor.name"
            :opacity="data.backgroundColorOpacity === undefined ? 25 : data.backgroundColorOpacity"
          />

          <common-link
            v-if="auction.notice"
            class="hidden text-red underline lg:inline-block"
            :href="auction.notice.url"
          >
            <span>{{ auction.notice.label }}</span>
          </common-link>
        </div>

        <div
          class="relative flex w-full flex-wrap justify-between gap-3 overflow-visible lg:w-auto lg:p-4"
        >
          <div
            class="flex flex-col lg:!pl-0"
            :class="{
              '[@media(min-width:350px)]:pl-14': urlToPreviousPage,
            }"
          >
            <div class="font-bold lg:font-normal">
              {{ $t('auction-list.auction-number') }} {{ auction.number }}
            </div>

            <StartDateCalendarPopper
              :title="auction.title"
              :type="auction.type"
              :department="auction.department"
              :start-date="auction.startDate"
              :app-id="auction.appId"
              :stage="auction.stage"
              :slug="auction.slug"
              :descriptions="auction.descriptions"
            >
              <div class="flex lg:flex-col">
                <div class="md:mr-0">
                  <span class="md:hidden">
                    {{
                      getDayName(auction.startDate, {
                        weekday: 'short',
                        lang: locale,
                      })
                    }}
                  </span>
                  <span class="hidden lg:inline-block">
                    {{
                      getDayName(auction.startDate, {
                        weekday: 'long',
                        lang: locale,
                      })
                    }}
                  </span>

                  {{ formatDate(auction.startDate) }}

                  <span class="md:hidden">&nbsp;|&nbsp;</span>
                </div>
                <div>
                  <span class="hidden lg:inline-block">{{ $t('start') }}</span>
                  {{
                    formatStartDate(auction.startDate, {
                      showFullDate: false,
                      $t,
                    })
                  }}
                </div>
              </div>
            </StartDateCalendarPopper>
            <DynamicAuctionListSharedCatalogPopper
              v-if="auction"
              :app-id="auction.appId"
              :catalog="auction.catalog"
              :restrictions="auction.restrictions"
              :type="auction.type"
              :stage="auction.stage"
            >
              <ElementsButton
                type="wide"
                size="tiny"
                class="mt-2 hidden lg:block !w-full text-primary"
                background-color="#C3D0DC"
              >
                {{ $t('auction-list.catalog') }}
              </ElementsButton>
            </DynamicAuctionListSharedCatalogPopper>
            <AuctionBannerBgText
              v-if="data.rightBackgroundColor?.name"
              :font-color="data.rightBackgroundColor.name"
              :opacity="data.backgroundColorOpacity === undefined ? 25 : data.backgroundColorOpacity"
            />
          </div>
        </div>

        <common-link
          v-if="auction.notice"
          class="text-bold inline-block self-start rounded-full bg-white px-2 pb-1 pt-2 text-center text-xs text-[#F59C00] text-red sm:px-3 sm:text-sm lg:hidden"
          :href="auction.notice.url"
        >
          <span>{{ auction.notice.label }}</span>
        </common-link>
      </div>

      <ElementsCarListButton
        v-if="pageType !== 'carList' && auctionId"
        :auction-id="auctionId"
        class="mr-1 lg:hidden"
      />
    </div>
  </header>

  <LazyDynamicAuctionBannerBmw
    v-if="data.layout === 'bmw' && auction"
    :auction="auction"
  />
</template>
